import React, { useState } from 'react';
import { PieChart, Pie, Cell, ResponsiveContainer, Sector } from 'recharts';

const ALLOCATIONS = [
  { name: 'Community & Ecosystem', pct: 45, color: '#8b5cf6' },
  { name: 'Liquidity Pool', pct: 20, color: '#3b82f6' },
  { name: 'Protocol Treasury', pct: 15, color: '#06b6d4' },
  { name: 'Founder (vested)', pct: 10, color: '#6366f1' },
  { name: 'Community Airdrop', pct: 5, color: '#10b981' },
  { name: 'Strategic Reserve', pct: 5, color: '#f59e0b' },
];

// Active slice highlight for tap/hover
function ActiveShape({ cx, cy, innerRadius, outerRadius, startAngle, endAngle, fill }) {
  return (
    <Sector
      cx={cx} cy={cy}
      innerRadius={innerRadius - 2}
      outerRadius={outerRadius + 4}
      startAngle={startAngle}
      endAngle={endAngle}
      fill={fill}
    />
  );
}

export default function TokenDonut({ supply }) {
  const [activeIndex, setActiveIndex] = useState(null);
  const currentSupply = supply?.uiAmount ?? 1_000_000_000;
  const burned = 1_000_000_000 - currentSupply;

  const data = burned > 0
    ? [...ALLOCATIONS, { name: 'Burned', pct: (burned / 1_000_000_000) * 100, color: '#ef4444' }]
    : ALLOCATIONS;

  return (
    <div className="bg-slate-900/60 border border-slate-800 rounded-xl p-3 sm:p-4 overflow-hidden">
      <h3 className="text-xs text-slate-500 uppercase tracking-wider mb-2">Token Distribution</h3>
      <div className="flex flex-col items-center sm:flex-row sm:items-start gap-3 sm:gap-4">
        {/* Chart */}
        <div className="w-32 h-32 sm:w-40 sm:h-40 flex-shrink-0">
          <ResponsiveContainer width="100%" height="100%">
            <PieChart>
              <Pie
                data={data}
                dataKey="pct"
                nameKey="name"
                cx="50%"
                cy="50%"
                innerRadius={35}
                outerRadius={58}
                paddingAngle={2}
                strokeWidth={0}
                activeIndex={activeIndex}
                activeShape={ActiveShape}
                onMouseEnter={(_, i) => setActiveIndex(i)}
                onMouseLeave={() => setActiveIndex(null)}
                onClick={(_, i) => setActiveIndex(activeIndex === i ? null : i)}
              >
                {data.map((entry, i) => (
                  <Cell key={i} fill={entry.color} />
                ))}
              </Pie>
            </PieChart>
          </ResponsiveContainer>
        </div>

        {/* Legend — always visible, doubles as tooltip on mobile */}
        <div className="space-y-1 sm:space-y-1.5 flex-1 w-full">
          {data.map((d, i) => (
            <div
              key={i}
              className={`flex items-center gap-2 text-[10px] sm:text-[11px] px-1.5 py-0.5 rounded transition-colors cursor-pointer ${
                activeIndex === i ? 'bg-slate-800/80' : ''
              }`}
              onMouseEnter={() => setActiveIndex(i)}
              onMouseLeave={() => setActiveIndex(null)}
              onClick={() => setActiveIndex(activeIndex === i ? null : i)}
            >
              <span className="w-2 h-2 rounded-full flex-shrink-0" style={{ backgroundColor: d.color }}></span>
              <span className="text-slate-400 flex-1 truncate">{d.name}</span>
              <span className="text-slate-300 font-mono flex-shrink-0">{d.pct}%</span>
              {activeIndex === i && (
                <span className="text-slate-500 font-mono flex-shrink-0 hidden sm:inline">
                  {(d.pct * 10_000_000).toLocaleString()}
                </span>
              )}
            </div>
          ))}
        </div>
      </div>
      <div className="mt-3 pt-3 border-t border-slate-800/50 flex flex-col sm:flex-row justify-between gap-1 text-[10px]">
        <span className="text-slate-500">Fixed supply: 1,000,000,000</span>
        <span className="text-slate-500">Mint authority: <span className="text-red-400">disabled</span></span>
      </div>
    </div>
  );
}
